class TabSection extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {HTMLElement[]} */
  #tabs = [];

  /** @type {HTMLElement[]} */
  #panels = [];

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const list = this.querySelector('.tabs__list');
    if (!list) return;

    const rows = [...this.querySelectorAll('.tabs__row')];
    if (!rows.length) return;

    this.#tabs = [];
    this.#panels = [];

    rows.forEach((row) => {
      const tab = row.querySelector('[role="tab"]');
      const panel = row.querySelector('[role="tabpanel"]');

      if (!(tab instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;

      list.appendChild(tab);
      this.#tabs.push(tab);
      this.#panels.push(panel);
    });

    if (!this.#tabs.length || !this.#panels.length) return;

    this.#tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.#activateTab(index), { signal });
      tab.addEventListener('keydown', (event) => this.#handleKeyDown(event, index), { signal });
    });

    const initialIndex = this.#tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    this.#activateTab(initialIndex >= 0 ? initialIndex : 0);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#tabs = [];
    this.#panels = [];
  }

  /**
   * @param {number} index
   */
  #activateTab(index) {
    this.#tabs.forEach((tab, tabIndex) => {
      const isActive = tabIndex === index;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.classList.toggle('tabs__tab--active', isActive);
    });

    this.#panels.forEach((panel, panelIndex) => {
      panel.hidden = panelIndex !== index;
    });
  }

  /**
   * @param {KeyboardEvent} event
   * @param {number} index
   */
  #handleKeyDown(event, index) {
    let newIndex = index;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        newIndex = (index + 1) % this.#tabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        newIndex = (index - 1 + this.#tabs.length) % this.#tabs.length;
        break;
      case 'Home':
        newIndex = 0;
        break;
      case 'End':
        newIndex = this.#tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#tabs[newIndex].focus();
    this.#activateTab(newIndex);
  }
}

if (!customElements.get('tab-section')) {
  customElements.define('tab-section', TabSection);
}
